import { carregarLiveKit } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/carregar-livekit'
import { fatiarPcmEmChunks } from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/pcm-em-chunks'
import {
  COMANDO,
  EVENTO,
  MOTIVO,
  MS_ATE_COMECAR_A_FALAR,
  MS_ATE_CONECTAR,
  MS_ENTRE_HEARTBEATS,
  SESSAO_CONECTADA,
  suportaWebSocket,
} from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/protocolo'

// ★ A SESSÃO do avatar ao vivo — o canal de comando e a sala de vídeo.
//
// Duas conexões, uma sessão: o **WebSocket** leva o áudio do nosso TTS ao
// provedor (`agent.speak`), e a **sala LiveKit** traz o vídeo de volta. As
// credenciais das duas vêm do NOSSO servidor; a chave do provedor nunca
// esteve aqui (ver `core/avatar_ao_vivo_liveavatar.py`).
//
// ## O contrato com quem chama
//
//   abrir(credenciais, ganchos) -> Promise<boolean>   (o CANAL abriu?)
//   falar(pcmBase64)            -> Promise<void>      (resolve ao TERMINAR)
//   interromper()               -> void
//   escutando(bool)             -> void
//   fechar()                    -> void
//   estaAberta()                -> boolean
//
// `falar` resolver no FIM da fala é o que deixa a fila de `fala-da-ia.js`
// funcionar sem mudar uma linha: ela já espera o áudio acabar antes de mandar
// a frase seguinte, e a ordem das frases sai de graça.
//
// ⚠️ **Nada aqui rejeita.** O avatar é opcional por construção: uma queda no
// meio da conversa não pode derrubar o turno, que é onde mora a resposta. O
// desfecho ruim é sempre "não falou" + `aoQueda(motivo)`.

/** Ids de utterance: um por frase, como o protocolo pede. */
let _sequencia = 0
const _novaFala = () => {
  _sequencia += 1
  return `fala-${Date.now()}-${_sequencia}`
}

/** Avisa um gancho do consumidor sem deixar que ele derrube o canal. */
const _avisar = (st, nome, ...args) => {
  const gancho = st.ganchos[nome]
  if (typeof gancho !== 'function') return
  try {
    gancho(...args)
  } catch (e) {
    // Um consumidor que estoura não pode derrubar o canal.
  }
}

/** Manda um comando. `false` quando o canal não está aberto. */
const _mandar = (st, tipo, extra) => {
  if (!st.canal || st.canal.readyState !== 1) return false
  try {
    st.canal.send(JSON.stringify(Object.assign({ type: tipo }, extra || {})))
    return true
  } catch (e) {
    return false
  }
}

/** Encerra a fala em curso, com ou sem sucesso. Idempotente. */
const _encerrarFala = (st) => {
  if (!st.fala) return
  const fala = st.fala
  st.fala = null
  clearTimeout(fala.prazo)
  fala.resolver()
}

export const criarSessaoAvatarAoVivo = () => {
  // Estado num objeto só: são oito campos que vivem juntos, e um `let` para
  // cada estouraria o teto de statements da fábrica sem informar nada.
  const st = {
    canal: null,
    sala: null,
    heartbeat: null,
    conectado: false,
    fechada: false,
    // A fala em curso: `{resolver, prazo, tetoDeFala}`. Uma de cada vez, por
    // desenho — o protocolo é serial (uma utterance aberta por sessão) e a
    // fila que nos alimenta também é.
    fala: null,
    ganchos: {},
  }

  // -------------------------------------------------------------------------
  // Eventos do canal
  // -------------------------------------------------------------------------

  /** Os que decidem se a sessão está de pé. */
  const tratarSessao = (evento, aoConectar) => {
    if (evento.state !== SESSAO_CONECTADA) return
    st.conectado = true
    aoConectar(true)
  }

  /** Os que decidem quando a fala atual acabou. */
  const tratarFala = (tipo) => {
    // O fim vem por DOIS caminhos, e os dois têm de soltar a fila: o natural
    // e a interrupção (que NÃO emite `speak_ended`, por contrato).
    if (tipo === EVENTO.TERMINOU_DE_FALAR || tipo === EVENTO.FOI_INTERROMPIDO) {
      _encerrarFala(st)
      return
    }
    // Chegou o começo: troca o prazo curto (o avatar pode não responder) pelo
    // longo (a fala dura o que o áudio durar).
    if (tipo === EVENTO.COMECOU_A_FALAR && st.fala) {
      clearTimeout(st.fala.prazo)
      st.fala.prazo = setTimeout(() => _encerrarFala(st), st.fala.tetoDeFala)
      // ★ E avisa quem pediu a fala. Este é o instante em que a BOCA começa a
      // se mexer — o único ponto honesto para começar o som, já que o provedor
      // publica só VÍDEO na sala (medido) e o áudio tem de sair daqui.
      // Começar antes deixaria a fala à frente dos lábios pelo tempo de
      // codificação e rede do vídeo.
      if (typeof st.fala.aoComecar === 'function') {
        try {
          st.fala.aoComecar()
        } catch (e) {
          // Som local que falha não pode derrubar a fala do avatar.
        }
      }
    }
  }

  const tratarEvento = (evento, aoConectar) => {
    const tipo = evento && evento.type
    if (tipo === EVENTO.ESTADO_DA_SESSAO) return tratarSessao(evento, aoConectar)
    if (tipo === EVENTO.ESTADO_DO_AVATAR) return _avisar(st, 'aoEstado', evento.new_state)
    if (tipo === EVENTO.ERRO) {
      _avisar(st, 'aoQueda', MOTIVO.ERRO_DO_PROVEDOR, (evento.error || {}).type || '')
      // Um erro sobre a fala em curso a deixaria pendurada para sempre.
      return _encerrarFala(st)
    }
    return tratarFala(tipo)
  }

  // -------------------------------------------------------------------------
  // A sala de vídeo — em PARALELO ao canal, e opcional
  // -------------------------------------------------------------------------

  const entrarNaSala = (credenciais) => {
    if (!credenciais.livekit_url || !credenciais.livekit_client_token) return
    carregarLiveKit().then((sdk) => {
      if (st.fechada) return
      if (!sdk) return _avisar(st, 'aoQueda', MOTIVO.SEM_SDK_DE_VIDEO)
      try {
        st.sala = new sdk.Room({ adaptiveStream: true, dynacast: true })
        // O avatar publica a faixa DEPOIS de entrarmos; ouvir a inscrição
        // cobre os dois tempos (já publicada / publicada depois).
        st.sala.on(sdk.RoomEvent.TrackSubscribed, (faixa) => {
          if (!faixa) return
          // ⚠️ O ÁUDIO da sala também é anexado, e isso não é detalhe: com o
          // avatar ligado, `fala-da-ia.js` para de tocar o `<audio>` local
          // (o destino virou o provedor). Quem faz som passa a ser a faixa
          // que volta da sala — e ela vem sincronizada com os lábios, que é
          // a razão de o áudio dar essa volta em vez de tocar aqui.
          if (faixa.kind === 'video') _avisar(st, 'aoVideo', faixa)
          if (faixa.kind === 'audio') _avisar(st, 'aoAudio', faixa)
        })
        return st.sala
          .connect(credenciais.livekit_url, credenciais.livekit_client_token)
          .catch(() => _avisar(st, 'aoQueda', MOTIVO.SALA_RECUSADA))
      } catch (e) {
        return _avisar(st, 'aoQueda', MOTIVO.SALA_RECUSADA)
      }
    })
  }

  // -------------------------------------------------------------------------
  // Abertura
  // -------------------------------------------------------------------------

  const ligarOuvintes = (decidir) => {
    st.canal.addEventListener('message', (quadro) => {
      let evento = null
      try {
        evento = JSON.parse(quadro.data)
      } catch (e) {
        return
      }
      tratarEvento(evento, decidir)
    })
    st.canal.addEventListener('close', () => {
      st.conectado = false
      // Fala presa num canal morto nunca receberia `speak_ended`.
      _encerrarFala(st)
      if (!st.fechada) _avisar(st, 'aoQueda', MOTIVO.CANAL_CAIU)
      decidir(false)
    })
    st.canal.addEventListener('error', () => {
      if (!st.fechada) _avisar(st, 'aoQueda', MOTIVO.CANAL_CAIU)
      decidir(false)
    })
    st.canal.addEventListener('open', () => {
      // ⚠️ NÃO resolve aqui: o protocolo só aceita comando depois de
      // `session.state_updated: connected`, que chega logo em seguida.
      // Resolver no `open` faria o primeiro `agent.speak` sair cedo e ser
      // descartado sem erro nenhum.
      st.heartbeat = setInterval(
        () => _mandar(st, COMANDO.MANTER_VIVA), MS_ENTRE_HEARTBEATS,
      )
    })
  }

  /**
   * @param {Object} credenciais o `value` da rota `/voz/avatar-ao-vivo`.
   * @param {Object} ganchos `{aoVideo(faixa), aoEstado(estado),
   *   aoQueda(motivo, detalhe)}`.
   * @returns {Promise<boolean>} o CANAL abriu? O vídeo é opcional: uma sala
   *   que não entra não impede o avatar de falar.
   */
  const abrir = (credenciais, ganchos) => {
    st.ganchos = ganchos || {}
    if (!suportaWebSocket() || !credenciais || !credenciais.ws_url) {
      _avisar(st, 'aoQueda', MOTIVO.SEM_WEBSOCKET)
      return Promise.resolve(false)
    }
    return new Promise((resolve) => {
      let decidido = false
      const decidir = (valor) => {
        if (decidido) return
        decidido = true
        clearTimeout(prazo)
        resolve(valor)
      }
      const prazo = setTimeout(() => {
        _avisar(st, 'aoQueda', MOTIVO.NAO_CONECTOU)
        decidir(false)
      }, MS_ATE_CONECTAR)

      try {
        st.canal = new WebSocket(credenciais.ws_url)
      } catch (e) {
        _avisar(st, 'aoQueda', MOTIVO.SEM_WEBSOCKET)
        decidir(false)
        return
      }
      ligarOuvintes(decidir)
      // Em PARALELO de propósito: o avatar falando sem aparecer ainda é uma
      // conversa; esperar o vídeo para começar a falar seria atrasar o que
      // importa.
      entrarNaSala(credenciais)
    })
  }

  // -------------------------------------------------------------------------
  // Fala
  // -------------------------------------------------------------------------

  const esperarOFimDaFala = (chunks, aoComecar) =>
    new Promise((resolve) => {
      // Dois prazos: o CURTO até `agent.speak_started` (o provedor pode nunca
      // começar) e o LONGO depois dele (a fala dura o que o áudio durar). Um
      // prazo fixo cortaria uma frase de 20 s pela metade; nenhum prazo
      // prenderia a fila para sempre.
      st.fala = {
        resolver: resolve,
        aoComecar,
        tetoDeFala: (chunks.length + 10) * 1000,
        prazo: setTimeout(() => _encerrarFala(st), MS_ATE_COMECAR_A_FALAR),
      }
    })

  /**
   * Manda uma frase inteira e resolve quando o avatar termina de falá-la.
   *
   * @param {string} pcmBase64 PCM 16-bit 24 kHz mono, como o servidor mandou.
   * @param {Object} [opcoes]
   * @param {Function} [opcoes.aoComecar] chamado no `agent.speak_started` —
   *   ver o comentário lá em cima: é onde o som local começa.
   * @returns {Promise<void>} resolve SEMPRE — inclusive sem ter falado nada.
   */
  const falar = (pcmBase64, { aoComecar } = {}) => {
    if (!st.conectado || st.fechada) return Promise.resolve()
    const chunks = fatiarPcmEmChunks(pcmBase64)
    if (!chunks.length) return Promise.resolve()

    // Fala nova com outra em curso: sela a anterior em vez de deixar duas
    // promessas vivas sobre um canal que é serial.
    _encerrarFala(st)

    const id = _novaFala()
    const mandou = chunks
      .map((audio) => _mandar(st, COMANDO.FALAR, { event_id: id, audio }))
      .some(Boolean)
    if (!mandou) return Promise.resolve()
    _mandar(st, COMANDO.FIM_DA_FALA, { event_id: id })
    return esperarOFimDaFala(chunks, aoComecar)
  }

  /** Barge-in: corta a fala em curso sem derrubar a sessão. */
  const interromper = () => {
    if (!st.conectado) return
    _mandar(st, COMANDO.INTERROMPER)
    // Não espera o `agent.speak_interrupted`: quem interrompeu já seguiu.
    _encerrarFala(st)
  }

  /** Pose de escuta — diz à pessoa, sem texto, que a vez é dela. */
  const escutando = (ligado) => {
    if (!st.conectado) return
    _mandar(st, ligado ? COMANDO.ESCUTANDO : COMANDO.PARAR_DE_ESCUTAR)
  }

  const fechar = () => {
    st.fechada = true
    st.conectado = false
    clearInterval(st.heartbeat)
    st.heartbeat = null
    _encerrarFala(st)
    try {
      if (st.sala) st.sala.disconnect()
      if (st.canal) st.canal.close()
    } catch (e) {
      // Já mortos: o desfecho é o mesmo.
    }
    st.sala = null
    st.canal = null
  }

  const estaAberta = () => st.conectado && !st.fechada

  return { abrir, falar, interromper, escutando, fechar, estaAberta }
}
